import {
  type MaintenanceEntry,
  type MaintenanceModule,
  statusMeta,
  moduleMeta,
} from '@/mocks/saGlobalMaintenanceCenter';

interface MaintenanceTableProps {
  entries: MaintenanceEntry[];
  onSelect: (entry: MaintenanceEntry) => void;
}

export default function MaintenanceTable({ entries, onSelect }: MaintenanceTableProps) {
  if (entries.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
          <i className="ri-tools-line text-foreground-400 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin mantenimientos</p>
        <p className="text-foreground-400 text-xs">No se encontraron mantenimientos con los filtros actuales.</p>
      </div>
    );
  }

  function getModulesDisplay(modules: MaintenanceModule[]) {
    if (!modules || modules.length === 0) return <span className="text-foreground-400 text-[10px]">—</span>;
    const isAll = modules.includes('all');
    if (isAll) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-200 text-foreground-500 text-[10px] font-semibold">
          <i className="ri-global-line text-[9px]"></i>
          Todos
        </span>
      );
    }
    return (
      <div className="flex items-center gap-1">
        {modules.map((md) => {
          const meta = moduleMeta[md];
          if (!meta) return null;
          return (
            <span
              key={md}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-background-200 text-foreground-500 text-[9px] font-medium"
              title={meta.label}
            >
              <i className={`${meta.icon} text-[9px]`}></i>
            </span>
          );
        })}
      </div>
    );
  }

  function getTypeDisplay(type: string) {
    if (type === 'global') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-[10px] font-semibold">
          <i className="ri-global-line text-[9px]"></i>
          Global
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 text-[10px] font-semibold">
        <i className="ri-stack-line text-[9px]"></i>
        Por Módulo
      </span>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">ID</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Nombre</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Tipo</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Módulos</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Inicio</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Duración</th>
              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Creado por</th>
              <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const status = statusMeta[entry.status];
              return (
                <tr
                  key={entry.id}
                  onClick={() => onSelect(entry)}
                  className="border-b border-background-200/60 hover:bg-background-100/50 transition-colors cursor-pointer group"
                >
                  <td className="px-4 py-3">
                    <span className="text-foreground-500 text-[10px] font-mono font-semibold">{entry.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-foreground-800 text-xs font-semibold line-clamp-1 max-w-[250px]">{entry.name}</p>
                      <p className="text-foreground-400 text-[10px] line-clamp-1 max-w-[250px] mt-0.5">{entry.description}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getTypeDisplay(entry.type)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>
                      <span className={`text-[10px] font-semibold ${status.color}`}>{status.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getModulesDisplay(entry.modules)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-600 text-[10px] whitespace-nowrap">
                      {new Date(entry.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                      <span className="text-foreground-400 ml-1">
                        {new Date(entry.startDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-600 text-[10px]">{entry.duration}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-500 text-[10px] line-clamp-1 max-w-[180px]">{entry.createdBy}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-background-200 text-foreground-500 group-hover:bg-foreground-950 group-hover:text-background-50 transition-colors">
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}